import {
  computed,
  effect,
  type EnvironmentProviders,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  finalize,
  forkJoin,
  type Observable,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';

import { environment } from '@env';

import { AuthStore, type EntityType } from '@core/auth';
import { type AppError } from '@core/http';

import { SKELETON_FADE_MS } from '@shared/skeleton';

import { ShellStore } from '@features/shell';

import { HomeService } from './home.service';
import {
  type Blocker,
  type FacilityCycle,
  type HomeDashboardData,
  type Invoice,
} from './home.types';
import { keepPreviousIfEqual } from './home.util';

export const HomeStore = signalStore(
  { providedIn: 'root' },

  environment.withDevtools('home'),

  withState({
    invoices: [] as readonly Invoice[],
    blockers: [] as readonly Blocker[],
    facilityCycles: [] as readonly FacilityCycle[],
    documentsReviewCount: 0,
    loading: false,
    isFadingOut: false,
    hasLoaded: false,
    lastError: null as AppError | null,
  }),

  withComputed(({ blockers, facilityCycles, loading, isFadingOut }) => ({
    blockersCount: computed(() => blockers().length),
    cyclesNeedActionCount: computed(
      () => facilityCycles().filter((cycle) => !cycle.checkedIn).length,
    ),
    showSkeleton: computed(() => loading() || isFadingOut()),
    isSkeletonFading: computed(() => isFadingOut()),
  })),

  withMethods((store) => {
    const homeService = inject(HomeService);
    const authStore = inject(AuthStore);
    const shellStore = inject(ShellStore);

    let fadeTimer: ReturnType<typeof setTimeout> | null = null;

    const clearFadeTimer = (): void => {
      if (fadeTimer == null) return;
      clearTimeout(fadeTimer);
      fadeTimer = null;
    };

    const beginSkeletonFadeOut = (): void => {
      clearFadeTimer();
      patchState(store, { loading: false, isFadingOut: true });
      fadeTimer = setTimeout(() => {
        patchState(store, { isFadingOut: false });
        fadeTimer = null;
      }, SKELETON_FADE_MS);
    };

    const resolveRole = (): EntityType | null =>
      authStore.currentEntityType() ?? authStore.roles()[0]?.entityType ?? null;

    const isCurrentContext = (contextKey: string): boolean =>
      authStore.authorizationContextKey() === contextKey;

    const resetDashboard = (): void => {
      clearFadeTimer();
      patchState(store, {
        invoices: [],
        blockers: [],
        facilityCycles: [],
        documentsReviewCount: 0,
        loading: false,
        isFadingOut: false,
        hasLoaded: false,
        lastError: null,
      });
      shellStore.clearNavNotificationCounts();
    };

    const applyDashboard = (data: HomeDashboardData): void => {
      patchState(store, {
        invoices: keepPreviousIfEqual(store.invoices(), data.invoices),
        blockers: keepPreviousIfEqual(store.blockers(), data.blockers),
        facilityCycles: keepPreviousIfEqual(store.facilityCycles(), data.facilityCycles),
        documentsReviewCount: data.documentsReviewCount,
        hasLoaded: true,
      });
      shellStore.setNavNotificationCounts({
        home: data.blockers.length,
        documents: data.documentsReviewCount,
      });
    };

    const orEmpty = <T>(request$: Observable<T>, empty: T, contextKey: string): Observable<T> =>
      request$.pipe(
        catchError((error: AppError) => {
          if (isCurrentContext(contextKey)) patchState(store, { lastError: error });
          return of(empty);
        }),
      );

    const syncDashboard = rxMethod<string | null>(
      pipe(
        distinctUntilChanged(),
        tap(() => resetDashboard()),
        switchMap((contextKey) => {
          if (contextKey == null || !authStore.hasUser()) return EMPTY;

          const role = resolveRole();
          patchState(store, {
            loading: true,
            lastError: role ? null : { status: 0, original: 'missing-home-role' },
          });

          return forkJoin({
            invoices: role ? orEmpty(homeService.getInvoices(role), [], contextKey) : of([]),
            blockers: role ? orEmpty(homeService.getBlockers(role), [], contextKey) : of([]),
            facilityCycles: orEmpty(homeService.getFacilityCycles(), [], contextKey),
            documentsReviewCount: orEmpty(homeService.getDocumentsReviewCount(), 0, contextKey),
          }).pipe(
            tapResponse({
              next: (data) => {
                if (!isCurrentContext(contextKey)) return;
                applyDashboard(data);
                beginSkeletonFadeOut();
              },
              error: (error: AppError) => {
                if (!isCurrentContext(contextKey)) return;
                patchState(store, { lastError: error });
                beginSkeletonFadeOut();
              },
            }),
            finalize(() => patchState(store, { loading: false })),
          );
        }),
      ),
    );

    return { syncDashboard };
  }),

  withHooks({
    onInit(store) {
      const authStore = inject(AuthStore);

      effect(() => {
        store.syncDashboard(authStore.authorizationContextKey());
      });
    },
  }),
);

export function provideHomeDashboard(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => inject(HomeStore));
}
